import {Skeleton, Table, nodeColumn} from '@sollapay/ui/components';

import {
  TrustAccountGridCardListSkeleton,
  TrustAccountStatusGroupHeaderSkeleton
} from '@/features/trust-accounts/components';

import {GuestFirmHeaderSkeleton} from './GuestFirmHeader';
import {
  GUEST_ACCOUNT_CARDS_CLASS,
  GUEST_ACCOUNT_COLUMN_WIDTHS,
  GUEST_ACCOUNT_TABLE_PROPS,
  GUEST_FIRM_CARD_CLASS,
  GUEST_FIRM_PANEL_CLASS
} from '../config';

import type {ListView} from '@sollapay/ui/components';
import type {FC, ReactNode} from 'react';

interface SkeletonRow {
  group: string;
}

const ROWS: SkeletonRow[] = [{group: 'active'}, {group: 'active'}, {group: 'setup'}];

const CELLS: Record<keyof typeof GUEST_ACCOUNT_COLUMN_WIDTHS, ReactNode> = {
  trustName: (
    <div className="flex items-center gap-4">
      <Skeleton className="size-8.5 shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  ),
  balance: <Skeleton className="h-5 w-24" />,
  type: (
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-3 w-14" />
    </div>
  ),
  invitedBy: (
    <div className="flex items-center gap-2">
      <Skeleton className="size-8 shrink-0 rounded-full" />
      <Skeleton className="h-4 w-24" />
    </div>
  ),
  sharedAt: <Skeleton className="h-4 w-20" />
};

const COLUMNS = (Object.keys(CELLS) as (keyof typeof CELLS)[]).map(id =>
  nodeColumn<SkeletonRow>({
    id,
    width: GUEST_ACCOUNT_COLUMN_WIDTHS[id],
    enableSorting: false,
    header: <Skeleton className="h-3 w-14" />,
    cell: () => CELLS[id]
  })
);

const getSkeletonGroupKey = (row: SkeletonRow) => row.group;

/** Same card shell, status dividers and column widths as the loaded list, so nothing shifts when data arrives. */
export const GuestAccountsListSkeleton: FC<{view?: ListView}> = ({view = 'table'}) => (
  <div data-slot="guest-accounts-list-skeleton" className="flex flex-col gap-5">
    {[0, 1].map(card => (
      <div key={card} className={GUEST_FIRM_CARD_CLASS}>
        <GuestFirmHeaderSkeleton />
        <div className={GUEST_FIRM_PANEL_CLASS}>
          {view === 'card' ? (
            <div className={GUEST_ACCOUNT_CARDS_CLASS}>
              <TrustAccountGridCardListSkeleton />
            </div>
          ) : (
            <Table
              columns={COLUMNS}
              data={ROWS}
              variant={GUEST_ACCOUNT_TABLE_PROPS.variant}
              minWidth={GUEST_ACCOUNT_TABLE_PROPS.minWidth}
              getGroupKey={getSkeletonGroupKey}
              renderGroupHeader={() => <TrustAccountStatusGroupHeaderSkeleton />}
            />
          )}
        </div>
      </div>
    ))}
  </div>
);
